import { AfterViewChecked, Component, forwardRef, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import 'tinymce/icons/default/icons.min.js';
import 'tinymce/plugins/advlist/plugin.min.js';
import 'tinymce/plugins/code/plugin.min.js';
import 'tinymce/plugins/image/plugin.min.js';
import 'tinymce/plugins/lists/plugin.min.js';
import 'tinymce/plugins/media/plugin.min.js';
import 'tinymce/plugins/paste/plugin.min.js';
import 'tinymce/plugins/textcolor/plugin.min.js';
import 'tinymce/themes/silver/theme.min.js';
import { ApiRoutesV1 } from '../../common/routes';
import { AjaxService } from '../../core/ajax.service';
import { CustomerVoiceRouterService } from '../../core/cv-router.service';
import { content_css } from './fonts';

interface Editor {
  getContent: () => string;
  setContent: (content: string) => string;
  insertContent: (content: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  on: Function;
}

declare let tinymce: any;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare let templatedata: any;

const HTML_EDITOR_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RichEditorComponent),
  multi: true,
};

@Component({
  selector: 'app-rich-editor',
  template: ` <textarea id="{{ elementId }}"></textarea> `,
  providers: [HTML_EDITOR_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RichEditorComponent implements AfterViewChecked, ControlValueAccessor, OnDestroy {
  // Adapted from component of same name in partner-center-client. This is a stripped down version

  @Input() elementId: string;
  @Input() menubar: any = false;
  @Input() toolbar: any = false;
  @Input() plugins: string;
  @Input() width: number;
  @Input() height: number;
  @Input() setup: any;
  // This is for uploading images
  @Input() showUploadTab = false;
  @Input() maxImageSize = 5000000;

  private initialValue: string;
  private editor: Editor;
  private onChange: any;
  private onTouched: any;
  private fileUploadUrl: string;
  private tinyMCEFileUploadSuccessCallback: any;
  private tinyMCEFileUploadFailureCallback: any;
  private supportedFileTypes: string[] = ['image/jpeg', 'image/png', 'image/gif'];
  private subscriptions: Subscription[] = [];

  constructor(
    private ajaxService: AjaxService,
    private customerVoiceRouter: CustomerVoiceRouterService,
  ) {}

  ngAfterViewChecked(): void {
    this.subscriptions.push(
      this.customerVoiceRouter
        .getRouteUrl(ApiRoutesV1.imageUploadUrl)
        .subscribe((imageUploadUrl) => (this.fileUploadUrl = imageUploadUrl)),
    );
    tinymce.baseURL = 'https://storage.googleapis.com/developers-vendasta-client/assets';
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    tinymce.init({
      forced_root_block: '',
      selector: '#' + this.elementId,
      width: this.width,
      height: this.height,
      menubar: this.menubar,
      toolbar: this.toolbar,
      toolbar_mode: 'wrap',
      plugins: this.plugins,
      paste_text_sticky: true,
      paste_remove_styles: true,
      paste_auto_cleanup_on_paste: true,
      convert_urls: false,
      allow_conditional_comments: true,
      valid_children: '+body[style]',
      branding: false,
      content_css: content_css,
      font_formats:
        'Arial=Arial;Open Sans=Open Sans;Roboto=Roboto;Lato=Lato;Montserrat=Montserrat;Oswald=Oswald;' +
        'Source Sans Pro=Source Sans Pro;Raleway=Raleway;PT Sans=PT Sans;Roboto Slab=Roboto Slab;Merriweather=Merriweather;' +
        'Ubuntu=Ubuntu;Playfair Display=Playfair Display;Noto Sans=Noto Sans;Poppins=Poppins;Lora=Lora;PT Serif=PT Serif;' +
        'Roboto Mono=Roboto Mono;Arimo=Arimo;Titillium Web=Titillium Web;Times New Roman=Times New Roman',
      fontsize_formats: '8px 10px 11px 12px 14px 18px 24px 36px',
      setup: (editor: any) => {
        this.editor = editor;

        editor.on('init', () => {
          if (this.initialValue) {
            this.editor.setContent(this.initialValue);
          }
          editor.execCommand('fontName', true, 'Arial');
        });

        // prevents this.editor from being undefined on events
        const updateHandler = () => {
          this.onEditorUpdate();
        };
        editor.on('keyup', updateHandler);
        editor.on('change', updateHandler);
        editor.on('NodeChange', updateHandler);

        if (this.setup) {
          this.setup(editor);
        }
      },
      images_upload_handler: function (blobInfo: any, success: any, failure: any): void {
        // Check if image is a valid type and size
        let validFileType = false;
        for (const supportedFileType of self.supportedFileTypes) {
          if (blobInfo.blob().type === supportedFileType) {
            validFileType = true;
          }
        }
        if (!validFileType) {
          failure('Filetype must be jpeg, gif or png');
          return;
        }
        if (blobInfo.blob().size > self.maxImageSize) {
          failure('Image must be less than 5 MB');
          return;
        }
        // set success and failure variables to proper context
        self.tinyMCEFileUploadSuccessCallback = success;
        self.tinyMCEFileUploadFailureCallback = failure;

        // start uploading image
        self.uploadImage(blobInfo);
      },
      //  We have to pass any non-empty string to show the uploader tab in tinymce image uploader.
      //  And since we are using images_upload_handler, this url is not being used at all.
      images_upload_url: this.showUploadTab ? 'any-non-empty-value' : '',
    });
  }

  onEditorUpdate(): void {
    if (!this.editor) {
      return;
    }
    const content = this.editor.getContent();
    if (this.onChange) {
      this.onChange(content);
    }
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      // need to cache this so it can set when editor is instantiated
      this.initialValue = value;
    }
  }

  getValue(): string {
    return this.editor.getContent();
  }

  insertAtCursor(text: string): void {
    this.editor.insertContent(text);
  }

  clearAndInsertText(text: string): void {
    this.editor.setContent('');
    if (text) {
      this.editor.insertContent(text);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private uploadImage(blobInfo: any): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const fileUploadBody = new FormData();
    fileUploadBody.append('file', blobInfo.blob(), blobInfo.filename());

    this.ajaxService.post(this.fileUploadUrl, fileUploadBody).subscribe({
      next: (result) => {
        self.tinyMCEFileUploadSuccessCallback(result.body.data.url);
      },
      error: () => {
        self.tinyMCEFileUploadFailureCallback('Failed to upload. Please try again');
      },
    });
  }

  ngOnDestroy(): void {
    tinymce.remove(this.editor);
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
